import { addMinutes, format, isBefore, parse, startOfDay } from "date-fns";
import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  DateCalendar,
  Grid,
  Tooltip,
  Typography,
} from "../../../components";
import { Minimal } from "../../../layouts";
import {
  AgendamentoServicosService,
  ConfiguracoesAgendaFuncionamentoService,
  notification,
  PessoaService,
  SessionService,
} from "../../../services";
import palette from "../../../styles/dark-theme/palette";
import { formatCelular, formatDate, responseErros } from "../../../utils";
import AddAgendamentoDialog from "./add-agendamento.dialog";
import FiltroAgendamentoMenu from "./filtro-agendamento.dialog";

const AgendamentoView = () => {
  const [loading, setLoading] = useState(false);
  const [dataSelecionada, setDataSelecionada] = useState(new Date());
  const [mesSelecionado, setMesSelecionado] = useState(new Date());
  const [openAgendamentoDialog, setOpenAgendamentoDialog] = useState(false);
  const [prestadorServicoList, setPrestadorServicoList] = useState([]);
  const [filtros, setFiltros] = useState({});
  const [agendamento, setAgendamento] = useState({});
  const [agendamentosList, setAgendamentosList] = useState([]);
  const [agendamentosMesList, setAgendamentosMesList] = useState([]);
  const [horariosFuncionamento, setHorariosFuncionamento] = useState([]);
  const [horariosList, setHorariosList] = useState([]);

  const [anchorElFiltros, setAnchorElFiltros] = useState(null);

  const pessoaService = new PessoaService();
  const agendamentoServicosService = new AgendamentoServicosService();
  const sessionService = new SessionService();
  const agendaFuncionamentoService =
    new ConfiguracoesAgendaFuncionamentoService();
  const dadosEmpresa = sessionService.getEmpresa();
  const configuracoes = sessionService.getConfiguracoes();

  const formatFilterDate = (day = new Date()) => {
    const porIntervalo = [];
    const inicioDoDia = new Date(day);
    inicioDoDia.setHours(0, 0, 0, 0);
    const fimDoDia = new Date(day);
    fimDoDia.setHours(23, 59, 59, 999);
    porIntervalo.push({
      coluna: "periodo",
      de: inicioDoDia.toISOString(),
      ate: fimDoDia.toISOString(),
    });
    return porIntervalo;
  };

  useEffect(() => {
    const porIntervalo = formatFilterDate();
    buscarAgendamentos({ porIntervalo });
    buscarAgendamentosMes({ mesReferencia: porIntervalo[0].de });
    buscarPrestadoresServico();
    buscarHorariosFuncionamento();
  }, []);

  async function buscarPrestadoresServico() {
    const filtros = {
      tiposPessoaId: 5,
      nonPaginated: true,
      ativado: true,
    };
    const res = await pessoaService.getAll(filtros);
    if (!res.isAxiosError) {
      setPrestadorServicoList(res.data);
    } else {
      responseErros(res);
    }
  }

  async function buscarAgendamentosMes(filter) {
    setLoading(true);
    const result = await agendamentoServicosService.getAllAgendamentosTotalMes({
      ...filtros,
      ...filter,
    });
    if (!result.isAxiosError) {
      setAgendamentosMesList(result.data);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  async function buscarAgendamentos(filter) {
    const filters = {
      ...filtros,
      ...filter,
      nonPaginated: true,
    };
    setLoading(true);
    const result = await agendamentoServicosService.getAllFiltroAvancado(
      filters,
    );
    if (!result.isAxiosError) {
      setAgendamentosList(result.data);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  async function buscarHorariosFuncionamento() {
    const res = await agendaFuncionamentoService.find();
    if (!res.isAxiosError) {
      setHorariosFuncionamento(res.data);
    } else {
      responseErros(res);
    }
  }

  const handleDeleteAgendamentos = async (id) => {
    const retornoAlerta = await notification.confirmacao(
      "Excluir!",
      `Tem certeza que deseja excluir esse Agendamento?`,
    );
    if (retornoAlerta.isConfirmed) {
      setLoading(true);
      const result = await agendamentoServicosService.deletar(id);
      if (!result.isAxiosError) {
        const porIntervalo = formatFilterDate(dataSelecionada);
        buscarAgendamentos({ porIntervalo });
        buscarAgendamentosMes({
          mesReferencia: new Date(dataSelecionada).toISOString(),
        });
        notification.deletadoSucesso();
      } else {
        setLoading(false);
        responseErros(result);
      }
    }
  };

  const handleFiltrosMenuOpen = (event) => {
    setAnchorElFiltros(event.currentTarget);
  };

  const renderIconFiltrosColor = () => {
    if (prestadorServicoList.length === 0) {
      return "lightgray";
    } else {
      return anchorElFiltros ? "white" : palette.primary.main;
    }
  };

  const formatPrestadorServico = (id) => {
    const prestador = prestadorServicoList.find(
      (prestador) => prestador.id === id,
    );
    return prestador?.nomeRazaoSocial;
  };

  function formatarHorariosDisponiveis(horariosAgendados, data) {
    const diaSemana = data.getDay(),
      intervalo = configuracoes.intervaloAgendamentos;
    const intervalosFuncionamento =
      horariosFuncionamento.find((item) => diaSemana === item.diaSemana)
        ?.intervalos ?? [];

    const horarios = [];
    for (const horario of intervalosFuncionamento) {
      let horaInicial = parse(horario.abertura, "HH:mm:ss", new Date()),
        horaFechamento = parse(horario.fechamento, "HH:mm:ss", new Date());

      while (horaInicial < horaFechamento) {
        const horarioString = format(horaInicial, "HH:mm");
        if (addMinutes(horaInicial, intervalo) <= horaFechamento) {
          if (!horariosAgendados?.includes(horarioString)) {
            horarios.push(horarioString);
          }
        }
        horaInicial = addMinutes(horaInicial, intervalo);
      }
    }
    return horarios;
  }

  function formatarHorariosAgendados(prestadorServico, horariosPrestador) {
    const agendamentosDia =
      horariosPrestador ??
      agendamentosList
        .filter(
          ({ prestadorServicoId }) => prestadorServicoId === prestadorServico,
        )
        .map(({ periodo, tempoServico }) => ({
          horario: format(new Date(periodo), "HH:mm"),
          tempoServico,
        }));
    const intervalo = configuracoes.intervaloAgendamentos;

    return agendamentosDia?.reduce((acc, curr) => {
      if (!curr.tempoServico) return [...acc, curr.horario];
      const proporcao = curr.tempoServico / intervalo;
      const horarios = [curr.horario];
      let dataAtual = parse(curr.horario, "HH:mm", new Date());

      for (let i = 1; i < proporcao; i++) {
        dataAtual = addMinutes(dataAtual, intervalo);
        const horarioFormatado = format(dataAtual, "HH:mm");
        horarios.push(horarioFormatado);
      }

      return [...acc, ...horarios];
    }, []);
  }

  function filtrarHorariosDisponiveis(
    prestadorServico,
    horariosPrestador = null,
    data = dataSelecionada,
  ) {
    const horariosAgendados = formatarHorariosAgendados(
      prestadorServico,
      horariosPrestador,
    );
    const horarios = formatarHorariosDisponiveis(horariosAgendados, data);
    if (!horarios.length) {
      notification.alertaGenericos("Não há horários disponíveis para este dia");
    }
    return horarios;
  }

  const handleAdicionarAgendamento = () => {
    setAgendamento({ data: dataSelecionada });
    setOpenAgendamentoDialog(true);
  };

  const shouldDisableDate = (date) => {
    return (
      configuracoes.diasSemanaFuncionamento &&
      !configuracoes.diasSemanaFuncionamento.includes(date.getDay())
    );
  };

  return (
    <Minimal
      title="Agendamentos"
      loading={loading}
      action={
        <div className="px-2 d-flex flex-row">
          <Button
            onClick={handleAdicionarAgendamento}
            bold
            variant="contained"
            fullWidth
            disableElevation
            disabled={isBefore(dataSelecionada, startOfDay(new Date()))}
          >
            ADICIONAR
          </Button>
        </div>
      }
      filterActions={
        <div className="px-2 d-flex flex-row">
          <Button
            className="mx-2"
            onClick={handleFiltrosMenuOpen}
            variant={anchorElFiltros ? "contained" : "outlined"}
            color="primary"
            fullWidth
            disableElevation
            disabled={prestadorServicoList?.length === 0}
            size="small"
            startIcon={
              <i
                className="ph-bold ph-funnel-simple"
                style={{
                  color: renderIconFiltrosColor(),
                  fontSize: 15,
                }}
              ></i>
            }
            endIcon={
              <i
                className="ph ph-caret-down"
                style={{
                  color: renderIconFiltrosColor(),
                  fontSize: 18,
                }}
              ></i>
            }
          >
            PRESTADORES
          </Button>
        </div>
      }
    >
      <Grid container spacing={2} sx={{ height: "100%" }}>
        <Grid item xs={8} sx={{ height: "100%" }}>
          <DateCalendar
            value={dataSelecionada}
            agendamentos={agendamentosMesList}
            shouldDisableDate={shouldDisableDate}
            onMonthChange={(value) => {
              setMesSelecionado(new Date(value).toISOString());
              return buscarAgendamentosMes({
                mesReferencia: new Date(value).toISOString(),
              });
            }}
            onChange={(value) => {
              const porIntervalo = formatFilterDate(value);
              buscarAgendamentos({ porIntervalo });
              setDataSelecionada(value);
            }}
          />
        </Grid>
        <Grid item xs={4} sx={{ mt: 2, height: "100%" }}>
          <Card sx={{ borderRadius: "18px", height: "100%" }}>
            <CardHeader
              title="Serviços Agendados"
              titleAlign="center"
              sx={{ paddingTop: "30px !important" }}
            />
            <CardContent
              className="d-flex flex-column gap-3 px-3 pt-3 overflow-y-scroll h-100"
              paddingBottom={10}
            >
              {agendamentosList.length > 0 ? (
                agendamentosList.map(
                  (
                    { id, clienteNome, periodo, celular, prestadorServicoId },
                    i,
                  ) => (
                    <div
                      key={i}
                      className="d-flex flex-column p-3 user-select-none position-relative"
                      style={{
                        rowGap: 10,
                        borderRadius: 25,
                        border: "solid 0.1rem #eeeeee",
                        paddingRight: 70,
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 10,
                          right: 10,
                          bottom: 10,
                          width: 40,
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                          borderRadius: 20,
                          border: "1px solid #e3e3e3",
                        }}
                      >
                        <Tooltip
                          title="Editar"
                          arrow
                          placement="left"
                          className="btn d-flex align-items-center justify-content-center p-0"
                          style={{
                            flex: 1,
                            borderBottom: "1px solid #e3e3e3",
                          }}
                        >
                          <button
                            className="border-0 bg-transparent"
                            onClick={() => {
                              setAgendamento({
                                ...agendamentosList[i],
                                periodo: null,
                                data: dataSelecionada,
                                hora: new Date(agendamentosList[i].periodo),
                              });
                              setOpenAgendamentoDialog(true);
                            }}
                          >
                            <i
                              className="ph-fill ph-pencil"
                              style={{
                                color: "#1976d2",
                              }}
                            />
                          </button>
                        </Tooltip>
                        <Tooltip
                          title="Excluir"
                          arrow
                          placement="left"
                          className="btn d-flex align-items-center justify-content-center p-0"
                          style={{ flex: 1 }}
                        >
                          <button
                            className="border-0 bg-transparent"
                            onClick={() => handleDeleteAgendamentos(id)}
                          >
                            <i
                              className="ph-fill ph-trash"
                              style={{
                                color: "#e44a42",
                              }}
                            />
                          </button>
                        </Tooltip>
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <span className="d-flex align-items-center gap-2">
                          <i
                            className="ph-bold ph-clock"
                            style={{ color: "#989aac", fontSize: 18 }}
                          />
                          {format(formatDate.received(periodo), "HH:mm")}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="d-flex align-items-center gap-2 fw-bold">
                          <i
                            className="ph-bold ph-user"
                            style={{ color: "#989aac", fontSize: 18 }}
                          />
                          {clienteNome}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="d-flex align-items-center gap-2">
                          <i
                            className="ph-bold ph-device-mobile"
                            style={{ color: "#989aac", fontSize: 18 }}
                          />
                          {formatCelular(celular)}
                        </span>
                      </div>
                      {prestadorServicoId && (
                        <div className="d-flex justify-content-between align-items-center">
                          <span
                            className="d-flex align-items-center gap-2"
                            style={{ fontSize: 13 }}
                          >
                            <i
                              className="ph-bold ph-identification-badge"
                              style={{ color: "#989aac", fontSize: 18 }}
                            />
                            {formatPrestadorServico(prestadorServicoId)}
                          </span>
                        </div>
                      )}
                    </div>
                  ),
                )
              ) : (
                <div
                  className="d-flex flex-column p-3 user-select-none 
                    justify-content-center align-items-center h-100"
                  style={{
                    rowGap: 10,
                    paddingRight: 70,
                    marginTop: -32,
                  }}
                >
                  <i
                    className="ph-duotone ph-calendar-x"
                    style={{ fontSize: 50, marginRight: 10 }}
                  ></i>
                  <Typography
                    variant="subtitle4"
                    sx={{
                      fontWeight: 600,
                      wordBreak: "break-word",
                    }}
                  >
                    Nenhum agendamento
                  </Typography>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 400,
                      wordBreak: "break-word",
                    }}
                  >
                    Não há agendamentos para esta data
                  </Typography>
                </div>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <FiltroAgendamentoMenu
        anchorEl={anchorElFiltros}
        setAnchorEl={setAnchorElFiltros}
        filtros={filtros}
        setFiltros={setFiltros}
        prestadorServicoList={prestadorServicoList}
        dataSelecionada={dataSelecionada}
        mesSelecionado={mesSelecionado}
        formatFilterDate={formatFilterDate}
        buscarAgendamentos={buscarAgendamentos}
        buscarAgendamentosMes={buscarAgendamentosMes}
      />
      <AddAgendamentoDialog
        open={openAgendamentoDialog}
        setOpen={setOpenAgendamentoDialog}
        dataSelecionada={dataSelecionada}
        setDataSelecionada={setDataSelecionada}
        agendamento={agendamento}
        setAgendamento={setAgendamento}
        agendamentosList={agendamentosList}
        setAgendamentosMesList={setAgendamentosMesList}
        horariosList={horariosList}
        setHorariosList={setHorariosList}
        horariosFuncionamento={horariosFuncionamento}
        filtrarHorariosDisponiveis={filtrarHorariosDisponiveis}
        buscarAgendamentos={buscarAgendamentos}
        buscarAgendamentosMes={buscarAgendamentosMes}
        formatFilterDate={formatFilterDate}
        dadosEmpresa={dadosEmpresa}
        setLoading={setLoading}
      />
    </Minimal>
  );
};

export default AgendamentoView;
